import { makeStyles } from "@mui/styles";
import { Popover } from "components/Popover/Popover";
import { containerSettingsLabel } from "containers/Resources/components/CommonActions/containerSettingsLabel";
import { getDocumentationUrl } from "containers/Documentation/documentationService";
import { useEffect, useState } from "react";
import { BundleSettings } from "./BundleSettings";
import { ContainerDashboardManager } from "./ContainerDashboardManager";
import { containerSettingsBus } from "./containerSettingsBus";
import "./ContainerSettingsDialog.scss";

/**
 * "Container settings" dialog, opened from a container's "…" ellipsis menu (via
 * {@link containerSettingsBus}). Hosts the container-level settings that should
 * not clutter the container overview screen.
 *
 * Currently it hosts the configurable-dashboards manager (which dashboards
 * appear as tabs on the container, and which one is the default). Per Stefan's
 * 2026-09-17 target design the version + bundle_repository controls (bundle
 * only) move in here in a follow-up, and are then removed from the container
 * screen itself.
 *
 * Rendered in the React tree on the container screen, so it keeps full store /
 * router context (unlike an imperative antd Modal.confirm). Uses the canonical
 * Popover dialog shell (ui-design-principle-modals-dialogs): 24px padding, a
 * bold in-body header, no corner ×, overflow visible so the add-dashboard
 * dropdown is not clipped. The dashboards manager uses a buffered form — edits
 * apply on an explicit Save, and Cancel discards — so it owns the Save/Cancel
 * footer (wired via `onClose`) rather than the dialog rendering its own button.
 */
export const ContainerSettingsDialog = ({
    recordId,
    isDisabled,
    isBundle,
    containerType,
}: {
    recordId?: string;
    isDisabled?: boolean;
    /** Bundle containers additionally expose version + bundle_repository here. */
    isBundle?: boolean;
    /**
     * Resource type of the container (project / bundle / workspace) — drives the
     * dialog title (e.g. "Project settings") so the user-facing wording uses the
     * concrete term rather than "container".
     */
    containerType?: string;
}) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!recordId) {
            return undefined;
        }
        return containerSettingsBus.onOpen((openRecordId) => {
            if (openRecordId === recordId) {
                // Defer the open by one macrotask. The dialog's Popover is
                // always mounted (only toggled via `open`), so its
                // useOutsideClick document-`click` listener is always active.
                // The "…"-menu item that triggers this open is portaled and its
                // MUI menu closes via a ~225ms Grow transition, so the menu item
                // is STILL in the DOM (isConnected) when the opening click
                // reaches `document` — opening synchronously here therefore lets
                // that same in-flight gesture read as an "outside click" and
                // instantly dismiss the just-opened dialog. Deferring to the
                // next tick opens it after the gesture (and the menu's close)
                // has fully settled. (The complementary `e.target.isConnected`
                // guard in utils.ts `useOutsideClick` covers the *other*
                // trigger: an in-dialog control like "Add" that removes itself
                // on click, whose target genuinely detaches mid-dispatch.)
                setTimeout(() => setIsOpen(true), 0);
            }
        });
    }, [recordId]);

    const useStyles = makeStyles(() => ({
        rootStyle: { display: "flex", justifyContent: "center", alignItems: "center" },
        paperStyle: { maxHeight: "80vh", width: "560px", overflow: "visible" },
    }));
    const classes = useStyles();

    if (!recordId) {
        return null;
    }

    return (
        <Popover
            isOpen={isOpen}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="containerSettings__popover"
            contentClassName="containerSettings__content"
            anchorReference="none"
            closeOnInsideClick={false}
            closeHandler={() => setIsOpen(false)}
        >
            <div className="containerSettings__header">
                {containerSettingsLabel(containerType)}
            </div>
            {isBundle && <BundleSettings recordId={recordId} isDisabled={isDisabled} />}
            <ContainerDashboardManager
                recordId={recordId}
                isDisabled={isDisabled}
                variant="dialog"
                onClose={() => setIsOpen(false)}
                description={
                    <p className="containerSettings__intro">
                        Choose which dashboards appear as tabs here and which one opens by
                        default. You can add several.{" "}
                        <a
                            href={getDocumentationUrl("/docs/dashboards")}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Learn more
                        </a>
                    </p>
                }
            />
        </Popover>
    );
};

export default ContainerSettingsDialog;
